import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Spinner, Alert } from "react-bootstrap";
import {
  fetchCertificationListAPI,
  searchCertificationAPI,
  deleteCertificationAPI,
} from "../api/api";
import { useAuth } from "../context/userAuth/UserAuthState";
import { BsSearch, BsPlus, BsPencil, BsX, BsFileEarmarkPdf, BsImage, BsTrash } from "react-icons/bs";

const PER_PAGE = 10;

const CertificationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.user?.is_user;
  const isSuperAdmin = role === "isAdmin" || role === "super_admin" || role === "pro_super_admin";
  const tokenRef = useRef(user?.authorization);
  useEffect(() => { tokenRef.current = user?.authorization; }, [user]);

  const [certifications, setCertifications] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState("");

  const fetchList = async (page) => {
    setLoadingList(true);
    setListError("");
    try {
      const res = await fetchCertificationListAPI({ page, per_page: PER_PAGE }, tokenRef.current);
      if (res && res.success) {
        setCertifications(res.certification || []);
        setTotalPages(Math.ceil((res.total || 1) / PER_PAGE));
      } else {
        setListError((res && res.message) || "Failed to load certifications.");
        setCertifications([]);
      }
    } catch {
      setListError("Failed to load certifications. Please try again.");
      setCertifications([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchList(currentPage); }, [currentPage]); // eslint-disable-line

  const handleSearch = async () => {
    if (!searchInput.trim()) { setSearchResult(null); setSearchError(""); return; }
    setSearchLoading(true); setSearchError(""); setSearchResult(null);
    try {
      const res = await searchCertificationAPI({ certification_search_id: searchInput.trim() }, tokenRef.current);
      if (res && res.success) setSearchResult(res.certification);
      else setSearchError((res && res.message) || "No certification found.");
    } catch { setSearchError("Search failed. Please try again."); }
    finally { setSearchLoading(false); }
  };

  const handleClearSearch = () => { setSearchInput(""); setSearchResult(null); setSearchError(""); };

  const openEditor = (cert) => {
    if (!cert?.id) return;
    if (!cert.template_id) {
      setListError("This certificate was not created from a template, so it cannot be edited in the form. Delete it and recreate from Create Certificate.");
      return;
    }
    navigate(`/create-certificate?id=${cert.id}`);
  };

  const handleDelete = async (cert) => {
    if (!isSuperAdmin) return;
    if (!window.confirm(`Delete certification ${cert.certification_search_id}?`)) return;

    const res = await deleteCertificationAPI({ id: cert.id }, tokenRef.current);
    if (res && res.success) {
      if (searchResult) handleClearSearch();
      fetchList(currentPage);
    } else {
      setListError((res && res.message) || "Failed to delete certification.");
    }
  };

  const renderFileCell = (url) => {
    if (!url) return <span className="cp-no-file">No file</span>;
    const isImage = /\.(png|jpe?g)$/i.test(url);
    return isImage ? (
      <a href={url} target="_blank" rel="noreferrer" className="cp-file-thumb" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt="cert" />
        <BsImage size={11} /> View
      </a>
    ) : (
      <a href={url} target="_blank" rel="noreferrer" className="cp-file-pdf" onClick={(e) => e.stopPropagation()}>
        <BsFileEarmarkPdf size={14} /> View PDF
      </a>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="cp-pagination">
        <button className="cp-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹ Prev</button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
          <button key={n} className={`cp-page-btn${n === currentPage ? " cp-page-btn--active" : ""}`} onClick={() => setCurrentPage(n)}>{n}</button>
        ))}
        <button className="cp-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next ›</button>
      </div>
    );
  };

  const displayList = searchResult ? [searchResult] : certifications;

  return (
    <DashboardLayout pageTitle="All Certifications">
      <div className="cp-header">
        <div>
          <h2 className="cp-header__title">All Certifications</h2>
          <p className="cp-header__sub">Create, edit, and regenerate certificate / card PDFs.</p>
        </div>
        <button className="cp-add-btn" onClick={() => navigate("/create-certificate")}>
          <BsPlus size={20} /> Add Certification
        </button>
      </div>

      <div className="cp-search-bar">
        <div className="cp-search-wrap">
          <BsSearch className="cp-search-icon" size={15} />
          <input
            type="text"
            className="cp-search-input"
            placeholder="Search by Certificate No. or ID No. (CNIC)..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          {searchInput && (
            <button className="cp-search-clear" onClick={handleClearSearch} aria-label="Clear">
              <BsX size={18} />
            </button>
          )}
        </div>
        <button className="cp-search-btn" onClick={handleSearch} disabled={searchLoading}>
          {searchLoading ? <Spinner size="sm" animation="border" /> : "Search"}
        </button>
      </div>

      {searchError && <div className="cp-search-error">{searchError}</div>}

      {listError && (
        <Alert variant="danger" onClose={() => setListError("")} dismissible className="mt-3">
          {listError}
        </Alert>
      )}

      {loadingList ? (
        <div className="cp-loading">
          <Spinner animation="border" style={{ color: "var(--blue)" }} />
          <span>Loading certifications…</span>
        </div>
      ) : (
        <div className="cp-table-wrap">
          <table className="cp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Unique ID</th>
                <th>Type</th>
                <th>Issue Date</th>
                <th>Expiry Date</th>
                <th>File</th>
                <th>Author</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayList.length === 0 ? (
                <tr>
                  <td colSpan={8} className="cp-empty">No certifications found.</td>
                </tr>
              ) : (
                displayList.map((cert, idx) => (
                  <tr
                    key={cert.id}
                    className="cp-table-row--clickable"
                    onClick={() => openEditor(cert)}
                    style={{ cursor: cert.template_id ? "pointer" : "default" }}
                  >
                    <td className="cp-td-num">{searchResult ? 1 : (currentPage - 1) * PER_PAGE + idx + 1}</td>
                    <td>{cert.certification_search_id ? <span className="cp-id-badge">{cert.certification_search_id}</span> : <span className="cp-no-file">Not available</span>}</td>
                    <td>{cert.card_type ? <span className="cp-type-badge">{cert.card_type}</span> : <span className="cp-no-file">Not available</span>}</td>
                    <td>{cert.issue_date || <span className="cp-no-file">Not available</span>}</td>
                    <td>{cert.expire_date}</td>
                    <td>{renderFileCell(cert.certification_url)}</td>
                    <td>
                      <span className="cp-author">Added: {cert.created_by_name || "Unknown"}</span>
                      <span className="cp-author">Edited: {cert.updated_by_name || "Unknown"}</span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button className="cp-edit-btn" onClick={() => openEditor(cert)}>
                        <BsPencil size={13} /> Edit
                      </button>
                      {isSuperAdmin && (
                        <button className="cp-delete-btn" onClick={() => handleDelete(cert)}>
                          <BsTrash size={13} /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!searchResult && renderPagination()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default CertificationsPage;
